import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, Button, Card, statusTone } from '../../components/ui';
import { api } from '../../lib/api';
import type { Org } from '../../lib/types';

interface Integrity {
  duplicates: {
    teamId: string;
    linkedOrgId: string | null;
    orgs: { id: string; name: string; slug: string; createdAt: string }[];
  }[];
  unreserved: { teamId: string; orgId: string }[];
}

export function OrgsPage() {
  const { t } = useTranslation();
  const [orgs, setOrgs] = useState<Org[] | null>(null);
  const [integrity, setIntegrity] = useState<Integrity | null>(null);
  useEffect(() => {
    void api<{ orgs: Org[] }>('/api/v1/superadmin/orgs').then((r) => setOrgs(r.orgs));
    void api<Integrity>('/api/v1/superadmin/integrity')
      .then(setIntegrity)
      .catch(() => setIntegrity(null));
  }, []);
  const soon = (iso: string | null | undefined) =>
    iso && new Date(iso).getTime() - Date.now() < 30 * 86_400_000;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">{t('sa.orgs')}</h1>
        <Link to="/superadmin/superadmins" className="text-sm text-emerald-700 underline">
          {t('sa.superadmins')}
        </Link>
        <Link to="/superadmin/kb" className="text-sm text-emerald-700 underline">
          {t('sakb.title')}
        </Link>
        <Link to="/superadmin/nutrients" className="text-sm text-emerald-700 underline">
          {t('nutrients.title', 'Nutrients')}
        </Link>
        <Link to="/superadmin/orgs/new" className="ml-auto">
          <Button>{t('sa.newOrg')}</Button>
        </Link>
      </div>
      {integrity && integrity.duplicates.length > 0 && (
        <Card title={t('sa.integrity')}>
          <ul className="space-y-2 text-sm">
            {integrity.duplicates.map((d) => (
              <li key={d.teamId}>
                <p className="text-red-700">{t('sa.duplicateTeam', { team: d.teamId })}</p>
                <ul className="ml-4 list-disc">
                  {d.orgs.map((o) => (
                    <li key={o.id}>
                      <Link className="text-emerald-700 underline" to={`/superadmin/orgs/${o.id}`}>
                        {o.name}
                      </Link>{' '}
                      <span className="font-mono text-xs text-slate-500">
                        {o.slug} · {o.createdAt.slice(0, 10)}
                      </span>{' '}
                      {d.linkedOrgId === o.id && <Badge tone="green">{t('sa.linked')}</Badge>}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="py-2">{t('sa.name')}</th>
              <th>{t('sa.slug')}</th>
              <th>{t('sa.adminEmail')}</th>
              <th>{t('sa.status')}</th>
              <th>{t('sa.subscription')}</th>
              <th>{t('sa.expires')}</th>
              <th>{t('sa.created')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {orgs?.map((o) => (
              <tr key={o.id}>
                <td className="py-2">
                  <Link className="text-emerald-700 underline" to={`/superadmin/orgs/${o.id}`}>
                    {o.name}
                  </Link>
                </td>
                <td className="font-mono text-xs">{o.slug}</td>
                <td>{o.adminEmail}</td>
                <td>
                  <Badge tone={statusTone(o.status)}>{o.status}</Badge>
                </td>
                <td>
                  {o.subscription
                    ? `${o.subscription.platform} · tier ${o.subscription.tier} · ${o.subscription.maxSeats} seats`
                    : '-'}
                </td>
                <td className={soon(o.subscription?.expiresAt) ? 'font-medium text-red-600' : ''}>
                  {o.subscription?.expiresAt?.slice(0, 10) ?? '-'}
                </td>
                <td>{o.createdAt.slice(0, 10)}</td>
              </tr>
            ))}
            {orgs?.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-slate-500">
                  -
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
